import React from 'react';

/**
 * CoreFlow Error Boundary
 * 
 * Catches render errors anywhere in the component tree below it.
 * Shows a recovery UI instead of a white screen.
 * 
 * Must be a class component — React doesn't support error boundaries in hooks.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Log to console for debugging
    console.error("[CoreFlow] Render error caught by ErrorBoundary:", error);
    console.error("[CoreFlow] Component stack:", errorInfo?.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.history.pushState({}, "", "/dashboard");
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          height: "100vh", background: "linear-gradient(150deg, #E8ECF4 0%, #DEE3F3 30%, #D5DCEE 60%, #DDE2F0 100%)",
          fontFamily: "'proxima-nova', -apple-system, sans-serif",
        }}>
          <div style={{
            background: "rgba(255,255,255,0.65)",
            backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)",
            borderRadius: 20,
            borderTop: "1.5px solid rgba(255,255,255,0.55)",
            borderLeft: "1.5px solid rgba(255,255,255,0.45)",
            borderRight: "1.5px solid rgba(255,255,255,0.25)",
            borderBottom: "1.5px solid rgba(255,255,255,0.20)",
            boxShadow: "0 8px 32px rgba(217,219,221,0.12), 0 2px 8px rgba(0,0,0,0.20)",
            padding: "48px 40px",
            maxWidth: 460,
            width: "90%",
            textAlign: "center",
          }}>
            {/* Icon */}
            <div style={{
              width: 56, height: 56, borderRadius: 16,
              background: "linear-gradient(135deg, #E53935, #EF5350)",
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 20px",
              boxShadow: "0 4px 16px rgba(229,57,53,0.3)",
              fontSize: 24,
            }}>
              Warning
            </div>

            {/* Title */}
            <h2 style={{
              fontSize: 20, fontWeight: 700, color: "#1A1D23",
              margin: "0 0 8px",
            }}>
              Something went wrong
            </h2>

            {/* Subtitle */}
            <p style={{
              fontSize: 14, color: "#FAFAFA", lineHeight: 1.5,
              margin: "0 0 24px",
            }}>
              An unexpected error occurred. Your data is safe — try refreshing or going back to the dashboard.
            </p>

            {/* Error detail (collapsed) */}
            {this.state.error && (
              <details style={{
                textAlign: "left", marginBottom: 24,
                background: "rgba(217,219,221,0.08)", borderRadius: 10,
                padding: "10px 14px", fontSize: 12, color: "#FAFAFA",
              }}>
                <summary style={{ cursor: "pointer", fontWeight: 700, color: "#FAFAFA", marginBottom: 6 }}>
                  Error details
                </summary>
                <code style={{ whiteSpace: "pre-wrap", wordBreak: "break-all", display: "block", marginTop: 8, color: "#E53935", fontSize: 11 }}>
                  {this.state.error.toString()}
                </code>
                {this.state.errorInfo?.componentStack && (
                  <code style={{ whiteSpace: "pre-wrap", wordBreak: "break-all", display: "block", marginTop: 8, fontSize: 10, color: "#FAFAFA", maxHeight: 120, overflow: "auto" }}>
                    {this.state.errorInfo.componentStack.slice(0, 500)}
                  </code>
                )}
              </details>
            )}

            {/* Action buttons */}
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button
                onClick={this.handleGoHome}
                style={{
                  padding: "10px 20px", borderRadius: 10,
                  background: "linear-gradient(135deg, #FAFAFA, #6B83D4)",
                  color: "#fff", border: "none", cursor: "pointer",
                  fontSize: 13, fontWeight: 700,
                  boxShadow: "0 2px 8px rgba(64,89,173,0.25)",
                  transition: "transform 0.15s",
                }}
                onMouseOver={e => e.target.style.transform = "translateY(-1px)"}
                onMouseOut={e => e.target.style.transform = "translateY(0)"}
              >
                Go to Dashboard
              </button>
              <button
                onClick={this.handleReload}
                style={{
                  padding: "10px 20px", borderRadius: 10,
                  background: "rgba(255,255,255,0.06)",
                  color: "#FAFAFA", border: "1.5px solid rgba(64,89,173,0.15)",
                  cursor: "pointer", fontSize: 13, fontWeight: 700,
                  transition: "transform 0.15s",
                }}
                onMouseOver={e => e.target.style.transform = "translateY(-1px)"}
                onMouseOut={e => e.target.style.transform = "translateY(0)"}
              >
                Refresh Page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
